// Time of day and weather as look data (decision 0034's palette per time, 0048's day-night cycle and weather): a
// colour script of keys at set hours, blended with a smoothstep between neighbours (the Wildbrush recipe: the light is
// authored, never computed physically), a sun and a moon on an orbit, and per-weather modifiers blended by the
// weather's weights. `createDayLook(table).at(hours, weather)` gives everything a frame needs as plain numbers;
// `indoor(profile, outdoor)` gives a room's light from the outdoor one. Pure: no three.js, no GL, so it is unit-tested
// in Node. The game owns every value (the keys, the orbit, the weather looks, the indoor profiles).
// Decision 0088 added two optional parts to a key, both blended like the rest and absent from every table before it
// (so those look exactly as they did): the haze's near distance and maximum per hour (haze only in the distance at one
// hour, nearer at another), and cloud shadow (its shape per hour and a drift integrated over the game's clock, so the
// same clock always gives the same shadows).
// IMP-0024 added a third: a colour grade on the surface per hour (grade.ts), so a game can quiet the day's colours and
// leave the night's. A key without one has none; a table without any gives the identity grade at every hour.
import { CLOUD_SOFTNESS, NO_CLOUD_SHADOW, type CloudShadow, type CloudShadowFrame } from "./cloud-shadow";
import { NO_GRADE, type ColourGrade, type GradeFrame } from "./grade";

/** An sRGB colour, each channel 0 to 1. */
export type Rgb = [number, number, number];
/** A unit vector. */
export type Dir = [number, number, number];

/** One key of the colour script, at `at` hours. Colours are hex (`#rrggbb`). */
export interface DayKey {
  at: number;
  /** The sky dome: overhead, at the horizon (also the haze's colour), below it. */
  sky: { top: string; horizon: string; ground: string };
  /** The key light at this hour: the sun's by day, the moon's by night. */
  sun: { colour: string; intensity: number };
  /** The sky and ground fill. */
  fill: { sky: string; ground: string; intensity: number };
  /** Haze density (per metre) and how much height thins it (0 to 1); optionally, this hour's near distance (m, where
   *  the haze starts) and maximum (0 to 1), else the table's `haze.near` and `haze.max`. A key that sets them blends
   *  into a neighbour that doesn't through the table's values. */
  haze: { density: number; height: number; near?: number; max?: number };
  /** Tone-mapping exposure. */
  exposure: number;
  /** Night lamps (windows, lanterns), 0 to 1. */
  lamps: number;
  /** Stars, 0 to 1. */
  stars: number;
  /** Light shafts' strength, 0 to 1. */
  shafts: number;
  /** Cloud colours, lit and in shade. */
  clouds: { lit: string; shade: string };
  /** Cloud shadow on the ground at this hour (optional; cloud-shadow.ts): its shape, and its drift (m per hour of the
   *  game's clock, along x and z). A key without it has none: its neighbours' strength fades to 0 toward it, keeping
   *  their shape and drift. */
  cloudShadow?: CloudShadow & { drift: [number, number] };
  /** The surface's colour grade at this hour (optional; grade.ts): its saturation and a tint. A key without one has
   *  none (saturation 1, white): its neighbours' grades fade toward the identity, so a grade set on the day's keys
   *  leaves every hour between two keys without one exactly as it was. */
  grade?: ColourGrade;
}

/** The sun's and the moon's paths. Angles in radians; azimuth 0 is +z, increasing toward +x. */
export interface DayOrbit {
  /** Sunrise and sunset, hours. */
  rise: number;
  set: number;
  /** The sun's highest elevation (at the middle of the day). */
  peak: number;
  /** The sun's azimuth at the middle of the day, and how far it swings from rise to set. */
  azimuth: number;
  swing: number;
  /** The key light never comes lower than this elevation (long shadows stay readable). */
  minLight: number;
  /** The moon's highest elevation, and its lead on the point opposite the sun (radians along the orbit). */
  moonPeak: number;
  moonLead: number;
}

/** What one weather does to the look; blended by the weather's weights. Factors multiply; tints mix toward a colour. */
export interface WeatherLook {
  sun: number;
  fill: number;
  haze: number;
  exposure: number;
  shafts: number;
  stars: number;
  /** The sky's colours (and the haze, which is the horizon) mixed toward `colour` by `amount`. */
  tint: { colour: string; amount: number };
  /** Cloud cover, 0 to 1. */
  cloud: number;
  /** Rain, 0 to 1 (the particles' rate). */
  rain: number;
  /** Factors on the cloud shadow's strength and coverage (optional, each default 1; an overcast weather might soften
   *  it, a broken sky spread it). */
  cloudShadow?: { strength?: number; coverage?: number };
}

/** A room's light: its own fill, how much of the outdoor light comes in, its exposure and lamps. */
export interface IndoorProfile {
  fill: { sky: string; ground: string; intensity: number };
  /** The share of the outdoor key and fill that reaches inside (windows, the door), 0 to 1. */
  daylight: number;
  exposure: number;
  /** The room's lamps' least glow (they glow at least this, and as much as outdoors at night). */
  lamps: number;
}

/** The whole table the game passes in. */
export interface DayTable {
  keys: readonly DayKey[];
  orbit: DayOrbit;
  /** The haze's fixed parts (density and height come from the keys). */
  haze: { near: number; base: number; falloff: number; max: number };
  /** Glowing faces' brightness. */
  glow: number;
  /** How dark fully wet ground goes, 0 to 1. */
  wetDark: number;
  weather: Readonly<Record<string, WeatherLook>>;
  indoor: Readonly<Record<string, IndoorProfile>>;
}

/** The weather as `at` takes it: one weather's id, or weights by id (summing to 1) with the ground's wetness. */
export type WeatherInput = string | { mix: Readonly<Record<string, number>>; wet?: number };

/** Everything a frame of the look needs, as numbers. */
export interface DayLook {
  hours: number;
  sky: { top: Rgb; horizon: Rgb; ground: Rgb };
  /** The sun's true place (the dome's disc) and its colour; elevation in radians (negative below the horizon). */
  sun: { dir: Dir; elevation: number; colour: Rgb };
  moon: { dir: Dir; elevation: number };
  /** The light that casts: toward the sun or the moon, clamped above `minLight`, dipped while it swaps. */
  key: { dir: Dir; colour: Rgb; intensity: number; from: "sun" | "moon" };
  fill: { sky: Rgb; ground: Rgb; intensity: number };
  haze: { colour: Rgb; near: number; density: number; base: number; falloff: number; height: number; max: number };
  exposure: number;
  glow: number;
  lamps: number;
  stars: number;
  shafts: number;
  /** The dome's cloud cover and colours, and the cloud shadow on the ground (strength 0 when no key sets one). */
  clouds: { cover: number; lit: Rgb; shade: Rgb; shadow: CloudShadowFrame };
  /** The surface's colour grade (the identity when no key sets one). */
  grade: GradeFrame;
  rain: number;
  /** Ground wetness 0 to 1 and the darkening it gives (wet × wetDark). */
  wet: number;
  wetDark: number;
  /** Set for an indoor look. */
  indoor: string | null;
}

/** The look for any hour and weather. */
export interface DayLookSource {
  /** The look at `hours` (wrapped into a day) under `weather`. The cloud shadow's drift runs on the game's clock,
   *  `clock.day × 24 + hours` (pass the clock's whole days, or the clock's total hours as `hours`): the same clock
   *  gives the same frame, and a save that restores the clock restores the shadows. Without `day` the drift starts
   *  again at midnight (unseen while no shadow shows then). */
  at(hours: number, weather?: WeatherInput, clock?: { day?: number }): DayLook;
  /** A room's light, from the outdoor look it opens onto. */
  indoor(profile: string, outdoor: DayLook): DayLook;
  readonly table: DayTable;
}

/** The sun below this elevation hands the key light to the moon (radians). */
export const KEY_SWAP_ELEVATION = -0.035;

/** Parse `#rrggbb` to sRGB 0 to 1. Throws on anything else (a content bug). */
export function hexRgb(hex: string): Rgb {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) throw new Error(`@rpg/look daylight: bad colour "${hex}"`);
  const n = parseInt(m[1]!, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

const mixRgb = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const smooth = (t: number): number => t * t * (3 - 2 * t);
const clamp = (v: number, a: number, b: number): number => (v < a ? a : v > b ? b : v);
const smoothstep = (a: number, b: number, x: number): number => smooth(clamp((x - a) / (b - a), 0, 1));
const wrap24 = (h: number): number => ((h % 24) + 24) % 24;

/** Throws on a table that can't work: no keys, keys outside the day or out of order, a bad orbit. */
export function validateDayTable(t: DayTable): void {
  if (!t.keys.length) throw new Error("@rpg/look daylight: no keys");
  for (let i = 0; i < t.keys.length; i++) {
    const k = t.keys[i]!;
    if (!(k.at >= 0 && k.at < 24)) throw new Error(`@rpg/look daylight: key at ${k.at} is outside [0, 24)`);
    if (i > 0 && !(k.at > t.keys[i - 1]!.at)) throw new Error(`@rpg/look daylight: keys out of order at ${k.at}`);
    for (const c of [k.sky.top, k.sky.horizon, k.sky.ground, k.sun.colour, k.fill.sky, k.fill.ground, k.clouds.lit, k.clouds.shade]) hexRgb(c);
    if (k.haze.near !== undefined && !(k.haze.near >= 0)) throw new Error(`@rpg/look daylight: key at ${k.at}: haze near ${k.haze.near} must be ≥ 0`);
    if (k.haze.max !== undefined && !(k.haze.max >= 0 && k.haze.max <= 1)) throw new Error(`@rpg/look daylight: key at ${k.at}: haze max ${k.haze.max} must be in [0, 1]`);
    const g = k.grade;
    if (g) {
      if (g.saturation !== undefined && !(g.saturation >= 0 && g.saturation <= 4)) throw new Error(`@rpg/look daylight: key at ${k.at}: grade saturation ${g.saturation} must be in [0, 4]`);
      if (g.tint !== undefined) hexRgb(g.tint);
    }
    const s = k.cloudShadow;
    if (s) {
      if (!(s.scale > 0)) throw new Error(`@rpg/look daylight: key at ${k.at}: cloud shadow scale ${s.scale} must be > 0`);
      if (!(s.strength >= 0 && s.strength <= 1 && s.coverage >= 0 && s.coverage <= 1)) throw new Error(`@rpg/look daylight: key at ${k.at}: cloud shadow strength and coverage must be in [0, 1]`);
      if (!(Array.isArray(s.drift) && s.drift.length === 2 && s.drift.every(Number.isFinite))) throw new Error(`@rpg/look daylight: key at ${k.at}: cloud shadow drift must be [x, z]`);
    }
  }
  const o = t.orbit;
  if (!(o.rise >= 0 && o.set > o.rise && o.set < 24)) throw new Error(`@rpg/look daylight: sunrise ${o.rise} and sunset ${o.set} must be 0 ≤ rise < set < 24`);
  if (!Object.keys(t.weather).length) throw new Error("@rpg/look daylight: no weather looks");
}

/** Planted defects for the blend's checks: `noMidnightWrap` holds the last key flat through midnight instead of
 *  blending it into the first (the night's colour script jumps at the first key); `staticClouds` leaves the cloud
 *  shadow where it started whatever the clock says (the drift's checks go red); `hazeIgnoresKey` takes the haze's
 *  near distance and maximum from the table whatever a key sets (the per-hour haze's checks go red); `gradeSpreads`
 *  gives a key without a grade its neighbour's instead of none (a day-only grade reaches the night: the grade's checks
 *  go red). */
export const DAYLIGHT_FAULTS = { noMidnightWrap: false, staticClouds: false, hazeIgnoresKey: false, gradeSpreads: false };

/** The two keys around `hours` and the smoothstepped weight of the second, wrapping past midnight. */
export function keysAround(keys: readonly DayKey[], hours: number): { a: DayKey; b: DayKey; t: number } {
  const h = wrap24(hours);
  const n = keys.length;
  if (n === 1) return { a: keys[0]!, b: keys[0]!, t: 0 };
  let i = n - 1;
  for (let j = 0; j < n; j++) if (keys[j]!.at <= h) i = j;
  const a = keys[i]!;
  if (DAYLIGHT_FAULTS.noMidnightWrap && (i === n - 1 || h < keys[0]!.at)) return { a, b: a, t: 0 };
  const b = keys[(i + 1) % n]!;
  // the gap, wrapping from the last key to the first through midnight
  const span = wrap24(b.at - a.at) || 24;
  const into = wrap24(h - a.at);
  return { a, b, t: smooth(clamp(into / span, 0, 1)) };
}

/**
 * How far the cloud shadow has drifted (m, along x and z) by `hours` of the game's clock (`day × 24 + hours`, from the
 * clock's start): the integral of the keys' drift, blended between keys with the colour script's smoothstep, so the
 * clouds speed up and slow down smoothly from key to key and the same clock always gives the same place. A key without
 * a cloud shadow takes its neighbour's drift on the stretch between them. [0, 0] when no key has one.
 */
export function cloudDrift(keys: readonly DayKey[], hours: number): [number, number] {
  if (DAYLIGHT_FAULTS.staticClouds || !keys.some((k) => k.cloudShadow)) return [0, 0];
  const n = keys.length;
  if (n === 1) {
    const v = keys[0]!.cloudShadow!.drift;
    return [v[0] * hours, v[1] * hours];
  }
  const driftOf = (k: DayKey, other: DayKey): readonly [number, number] => k.cloudShadow?.drift ?? other.cloudShadow?.drift ?? [0, 0];
  // whole days from the first key, then the stretches of this day up to the hour
  const k0 = keys[0]!.at;
  const days = Math.floor((hours - k0) / 24);
  const into = hours - k0 - days * 24;
  let dayX = 0;
  let dayZ = 0;
  let x = 0;
  let z = 0;
  for (let i = 0; i < n; i++) {
    const a = keys[i]!;
    const b = keys[(i + 1) % n]!;
    const start = a.at - k0;
    const span = wrap24(b.at - a.at) || 24;
    const da = driftOf(a, b);
    const db = driftOf(b, a);
    // ∫ lerp(da, db, smooth(s)) ds over the whole stretch: the mean of its ends
    const fullX = (span * (da[0] + db[0])) / 2;
    const fullZ = (span * (da[1] + db[1])) / 2;
    dayX += fullX;
    dayZ += fullZ;
    const u = (into - start) / span;
    if (u >= 1) {
      x += fullX;
      z += fullZ;
    } else if (u > 0) {
      // ∫₀ᵘ smooth(s) ds = u³ − u⁴/2
      const g = u * u * u - (u * u * u * u) / 2;
      x += span * (da[0] * u + (db[0] - da[0]) * g);
      z += span * (da[1] * u + (db[1] - da[1]) * g);
    }
  }
  return [days * dayX + x, days * dayZ + z];
}

/** The angle along the sun's orbit: 0 at sunrise, π at sunset, 2π at the next sunrise (the night half is as long as
 *  the night). */
export function orbitAngle(o: DayOrbit, hours: number): number {
  const h = wrap24(hours);
  const dayLen = o.set - o.rise;
  if (h >= o.rise && h <= o.set) return (Math.PI * (h - o.rise)) / dayLen;
  return Math.PI + (Math.PI * wrap24(h - o.set)) / (24 - dayLen);
}

function dirOf(elevation: number, azimuth: number): Dir {
  const c = Math.cos(elevation);
  return [Math.sin(azimuth) * c, Math.sin(elevation), Math.cos(azimuth) * c];
}

/** The sun's and the moon's elevation and direction at `hours`. */
export function bodiesAt(o: DayOrbit, hours: number): { sun: { dir: Dir; elevation: number }; moon: { dir: Dir; elevation: number } } {
  const phi = orbitAngle(o, hours);
  const sunEl = o.peak * Math.sin(phi);
  const sunAz = o.azimuth + o.swing * (phi / Math.PI - 0.5);
  const mphi = phi + Math.PI + o.moonLead;
  const moonEl = o.moonPeak * Math.sin(mphi);
  const moonAz = o.azimuth + o.swing * ((mphi % (2 * Math.PI)) / Math.PI - 0.5);
  return { sun: { dir: dirOf(sunEl, sunAz), elevation: sunEl }, moon: { dir: dirOf(moonEl, moonAz), elevation: moonEl } };
}

/** The weather's weights from `at`'s input (one id → weight 1). Unknown ids throw. */
function weightsOf(t: DayTable, w: WeatherInput | undefined): { mix: [WeatherLook, number][]; wet: number } {
  const input = w === undefined ? { mix: { [Object.keys(t.weather)[0]!]: 1 } } : typeof w === "string" ? { mix: { [w]: 1 } } : w;
  const mix: [WeatherLook, number][] = [];
  let total = 0;
  for (const [id, weight] of Object.entries(input.mix)) {
    const l = t.weather[id];
    if (!l) throw new Error(`@rpg/look daylight: no weather look "${id}"`);
    if (weight > 0) {
      mix.push([l, weight]);
      total += weight;
    }
  }
  if (!(total > 0)) throw new Error("@rpg/look daylight: the weather's weights sum to 0");
  for (const m of mix) m[1] /= total;
  return { mix, wet: clamp(typeof w === "object" ? (w.wet ?? 0) : 0, 0, 1) };
}

/** The haze's near distance or maximum at the blend: each key's own, else the table's (exactly the table's when
 *  neither key sets it). */
function hazePart(t: DayTable, a: DayKey, b: DayKey, k: number, part: "near" | "max"): number {
  const va = a.haze[part];
  const vb = b.haze[part];
  if (DAYLIGHT_FAULTS.hazeIgnoresKey || (va === undefined && vb === undefined)) return t.haze[part];
  return lerp(va ?? t.haze[part], vb ?? t.haze[part], k);
}

/** The cloud shadow at the blend, under the weather's factors, drifted to the clock's `total` hours. */
function cloudShadowOf(t: DayTable, a: DayKey, b: DayKey, k: number, total: number, factor: (f: (l: WeatherLook) => number) => number): CloudShadowFrame {
  const ca = a.cloudShadow;
  const cb = b.cloudShadow;
  if (!ca && !cb) return { ...NO_CLOUD_SHADOW, offset: [0, 0] };
  // a key without one fades the strength to 0 and keeps its neighbour's shape
  const x = ca ?? cb!;
  const y = cb ?? ca!;
  return {
    strength: clamp(lerp(ca ? ca.strength : 0, cb ? cb.strength : 0, k) * factor((l) => l.cloudShadow?.strength ?? 1), 0, 1),
    coverage: clamp(lerp(x.coverage, y.coverage, k) * factor((l) => l.cloudShadow?.coverage ?? 1), 0, 1),
    scale: lerp(x.scale, y.scale, k),
    softness: lerp(x.softness ?? CLOUD_SOFTNESS, y.softness ?? CLOUD_SOFTNESS, k),
    offset: cloudDrift(t.keys, total),
  };
}

/** The grade at the blend: each key's own, a key without one the identity (with `gradeSpreads`, its neighbour's). The
 *  identity exactly when neither key sets one. */
function gradeOf(a: DayKey, b: DayKey, k: number): GradeFrame {
  let ga = a.grade;
  let gb = b.grade;
  if (!ga && !gb) return { saturation: NO_GRADE.saturation, tint: [...NO_GRADE.tint] };
  if (DAYLIGHT_FAULTS.gradeSpreads) {
    ga = ga ?? gb;
    gb = gb ?? ga;
  }
  const sat = (g: ColourGrade | undefined): number => g?.saturation ?? 1;
  const tint = (g: ColourGrade | undefined): Rgb => (g?.tint ? hexRgb(g.tint) : [1, 1, 1]);
  return { saturation: lerp(sat(ga), sat(gb), k), tint: mixRgb(tint(ga), tint(gb), k) };
}

/** The look at `hours` under `weather` (see {@link DayLookSource.at}). */
export function dayLookAt(t: DayTable, hours: number, weather?: WeatherInput, clock?: { day?: number }): DayLook {
  const { a, b, t: k } = keysAround(t.keys, hours);
  const col = (f: (key: DayKey) => string): Rgb => mixRgb(hexRgb(f(a)), hexRgb(f(b)), k);
  const num = (f: (key: DayKey) => number): number => lerp(f(a), f(b), k);
  const w = weightsOf(t, weather);
  const sum = (f: (l: WeatherLook) => number): number => w.mix.reduce((acc, [l, wt]) => acc + f(l) * wt, 0);

  let top = col((x) => x.sky.top);
  let horizon = col((x) => x.sky.horizon);
  let ground = col((x) => x.sky.ground);
  let keyColour = col((x) => x.sun.colour);
  let fillSky = col((x) => x.fill.sky);
  // the weather's tints, each by its weight
  for (const [l, wt] of w.mix) {
    const tint = hexRgb(l.tint.colour);
    const amt = l.tint.amount * wt;
    top = mixRgb(top, tint, amt);
    horizon = mixRgb(horizon, tint, amt);
    ground = mixRgb(ground, tint, amt);
    keyColour = mixRgb(keyColour, tint, amt * 0.5);
    fillSky = mixRgb(fillSky, tint, amt * 0.5);
  }

  const bodies = bodiesAt(t.orbit, hours);
  const fromSun = bodies.sun.elevation > KEY_SWAP_ELEVATION;
  const src = fromSun ? bodies.sun : bodies.moon;
  // the key never drops under minLight, and dips to nothing as it swaps between the sun and the moon (no visible flip)
  const el = Math.max(t.orbit.minLight, src.elevation);
  const az = Math.atan2(src.dir[0], src.dir[2]);
  const dip = smoothstep(0, 0.06, Math.abs(bodies.sun.elevation - KEY_SWAP_ELEVATION));
  const cover = clamp(sum((l) => l.cloud), 0, 1);
  return {
    hours: wrap24(hours),
    sky: { top, horizon, ground },
    sun: { dir: bodies.sun.dir, elevation: bodies.sun.elevation, colour: keyColour },
    moon: bodies.moon,
    key: { dir: dirOf(el, az), colour: keyColour, intensity: num((x) => x.sun.intensity) * sum((l) => l.sun) * dip, from: fromSun ? "sun" : "moon" },
    fill: { sky: fillSky, ground: col((x) => x.fill.ground), intensity: num((x) => x.fill.intensity) * sum((l) => l.fill) },
    haze: { colour: horizon, near: hazePart(t, a, b, k, "near"), density: num((x) => x.haze.density) * sum((l) => l.haze), base: t.haze.base, falloff: t.haze.falloff, height: num((x) => x.haze.height), max: hazePart(t, a, b, k, "max") },
    exposure: num((x) => x.exposure) * sum((l) => l.exposure),
    glow: t.glow,
    lamps: clamp(num((x) => x.lamps), 0, 1),
    stars: clamp(num((x) => x.stars) * sum((l) => l.stars), 0, 1),
    shafts: clamp(num((x) => x.shafts) * sum((l) => l.shafts), 0, 1),
    clouds: { cover, lit: col((x) => x.clouds.lit), shade: col((x) => x.clouds.shade), shadow: cloudShadowOf(t, a, b, k, (clock?.day ?? 0) * 24 + hours, sum) },
    grade: gradeOf(a, b, k),
    rain: clamp(sum((l) => l.rain), 0, 1),
    wet: w.wet,
    wetDark: w.wet * t.wetDark,
    indoor: null,
  };
}

/** A room's look from the outdoor one (see {@link DayLookSource.indoor}). The outdoor key's grade, if any, carries in. */
export function indoorLook(t: DayTable, profile: string, outdoor: DayLook): DayLook {
  const p = t.indoor[profile];
  if (!p) throw new Error(`@rpg/look daylight: no indoor profile "${profile}"`);
  const d = clamp(p.daylight, 0, 1);
  const own = p.fill.intensity;
  const out = outdoor.fill.intensity * d;
  const share = own + out > 0 ? out / (own + out) : 0;
  return {
    ...outdoor,
    key: { ...outdoor.key, intensity: outdoor.key.intensity * d },
    fill: { sky: mixRgb(hexRgb(p.fill.sky), outdoor.fill.sky, share), ground: mixRgb(hexRgb(p.fill.ground), outdoor.fill.ground, share), intensity: own + out },
    haze: { ...outdoor.haze, density: 0 },
    exposure: p.exposure,
    lamps: Math.max(outdoor.lamps, clamp(p.lamps, 0, 1)),
    shafts: 0,
    rain: 0,
    wet: 0,
    wetDark: 0,
    indoor: profile,
  };
}

/**
 * A look part of the way from `a` to `b` (t 0 is `a`, 1 is `b`): every number and colour mixed, directions mixed and
 * renormalised, the key's source and the indoor profile taken from whichever side is nearer. For a light that changes
 * over a moment rather than at once (walking into a room, out of it).
 */
export function mixDayLook(a: DayLook, b: DayLook, t: number): DayLook {
  const k = clamp(t, 0, 1);
  if (k <= 0) return a;
  if (k >= 1) return b;
  const n = (x: number, y: number): number => x + (y - x) * k;
  const dir = (x: Dir, y: Dir): Dir => {
    const v: Dir = [n(x[0], y[0]), n(x[1], y[1]), n(x[2], y[2])];
    const l = Math.hypot(v[0], v[1], v[2]);
    return l > 1e-9 ? [v[0] / l, v[1] / l, v[2] / l] : y;
  };
  const near = k < 0.5 ? a : b;
  return {
    hours: b.hours,
    sky: { top: mixRgb(a.sky.top, b.sky.top, k), horizon: mixRgb(a.sky.horizon, b.sky.horizon, k), ground: mixRgb(a.sky.ground, b.sky.ground, k) },
    sun: { dir: dir(a.sun.dir, b.sun.dir), elevation: n(a.sun.elevation, b.sun.elevation), colour: mixRgb(a.sun.colour, b.sun.colour, k) },
    moon: { dir: dir(a.moon.dir, b.moon.dir), elevation: n(a.moon.elevation, b.moon.elevation) },
    key: { dir: dir(a.key.dir, b.key.dir), colour: mixRgb(a.key.colour, b.key.colour, k), intensity: n(a.key.intensity, b.key.intensity), from: near.key.from },
    fill: { sky: mixRgb(a.fill.sky, b.fill.sky, k), ground: mixRgb(a.fill.ground, b.fill.ground, k), intensity: n(a.fill.intensity, b.fill.intensity) },
    haze: {
      colour: mixRgb(a.haze.colour, b.haze.colour, k),
      near: n(a.haze.near, b.haze.near),
      density: n(a.haze.density, b.haze.density),
      base: n(a.haze.base, b.haze.base),
      falloff: n(a.haze.falloff, b.haze.falloff),
      height: n(a.haze.height, b.haze.height),
      max: n(a.haze.max, b.haze.max),
    },
    exposure: n(a.exposure, b.exposure),
    glow: n(a.glow, b.glow),
    lamps: n(a.lamps, b.lamps),
    stars: n(a.stars, b.stars),
    shafts: n(a.shafts, b.shafts),
    clouds: {
      cover: n(a.clouds.cover, b.clouds.cover),
      lit: mixRgb(a.clouds.lit, b.clouds.lit, k),
      shade: mixRgb(a.clouds.shade, b.clouds.shade, k),
      shadow: {
        strength: n(a.clouds.shadow.strength, b.clouds.shadow.strength),
        coverage: n(a.clouds.shadow.coverage, b.clouds.shadow.coverage),
        scale: n(a.clouds.shadow.scale, b.clouds.shadow.scale),
        softness: n(a.clouds.shadow.softness, b.clouds.shadow.softness),
        offset: [n(a.clouds.shadow.offset[0], b.clouds.shadow.offset[0]), n(a.clouds.shadow.offset[1], b.clouds.shadow.offset[1])],
      },
    },
    grade: { saturation: n(a.grade.saturation, b.grade.saturation), tint: mixRgb(a.grade.tint, b.grade.tint, k) },
    rain: n(a.rain, b.rain),
    wet: n(a.wet, b.wet),
    wetDark: n(a.wetDark, b.wetDark),
    indoor: near.indoor,
  };
}

/** The look source over a table (validated once). */
export function createDayLook(table: DayTable): DayLookSource {
  validateDayTable(table);
  return { table, at: (h, w, c) => dayLookAt(table, h, w, c), indoor: (p, o) => indoorLook(table, p, o) };
}
